import {Disclosure, Menu, Transition} from "@headlessui/react";
import {
  Bars3Icon,
  ChevronDownIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";
import {loader} from "@monaco-editor/react";
import React, {Fragment, useEffect, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {getMyRoles} from "../../services/userService";
import {User} from "../auth/User";
import {
  aboutIcon,
  adminIcon,
  assignmentIcon,
  customPronunciationIcon,
  dialerIcon,
  docIcon,
  outreachIcon,
  personaIcon,
  presentationIcon,
  renderMenuIcon,
  sessionIcon,
  settingsIcon,
  puzzlePieceIcon,
  skillIcon,
  topicIcon,
  twilioSenderIcon,
  userMonitorIcon,
} from "../common/IconLibrary";
import UserIcon from "../common/UserIcon";
import {useMenu} from "./MenuContext";

const ENVIRONMENT_CONFIG = {
  TITLE_SEPARATOR: "-",
  DEFAULT_ENV: "",
};

export const navigation = [
  {
    name: "Topics",
    href: "/topics",
    icon: topicIcon,
    hint: "Task-oriented prompt sets with pre-, main, and post-processing components",
  },
  {
    name: "Fragments",
    href: "/fragments",
    icon: puzzlePieceIcon,
    hint: "Global configuration for AI agent platform",
  },
  {
    name: "Personas",
    href: "/personas",
    icon: personaIcon,
    hint: "Customizable identities for interactions",
  },
  {
    name: "Custom Pronunciations",
    href: "/custom_pronunciations",
    icon: customPronunciationIcon,
    hint: "Manage custom pronunciations for AI agent",
    dividerAfter: true,
  },
  {
    name: "On-Demand",
    href: "/ondemand",
    icon: dialerIcon,
    hint: "On-demand validation of AI capabilities",
  },
  {
    name: "Presentations",
    href: "/presentations",
    icon: presentationIcon,
    hint: "External presentations of AI capabilities",
    dividerAfter: true,
  },
  {
    name: "Sessions",
    href: "/sessions",
    icon: sessionIcon,
    hint: "Detailed history of past interactions",
  },
  {
    name: "Assignments",
    href: "/assignments",
    icon: assignmentIcon,
    hint: "Assignments for AI tasks and operations",
  },
  {
    name: "Outreach Monitor",
    href: "/admin/outreach",
    icon: outreachIcon,
    hint: "Dashboard of outreach call and text volumes by period",
    roles: ["admin", "data-analyst"],
  },
  {
    name: "Admin",
    hint: "Manage active AI operations",
    icon: adminIcon,
    roles: ["admin"],
    submenu: [
      {
        name: "User Monitor",
        href: "/user/monitor",
        icon: userMonitorIcon,
        hint: "Analysis of user interactions with the AI agent",
        dividerAfter: true,
      },
      {
        name: "Skills",
        href: "/skills",
        icon: skillIcon,
        hint: "Capabilities for pre- and post-processing tasks and interactions",
      },
      {
        name: "Settings",
        href: "/settings",
        icon: settingsIcon,
        hint: "Global configuration for AI agent platform",
      },
      {
        name: "Twilio RCS",
        href: "/twilio_sender",
        icon: twilioSenderIcon,
        hint: "Manage RCS senders and campaigns",
      },
    ],
  },
  {
    name: "Reference",
    href: "/doc",
    icon: docIcon,
    hint: "System documentation",
  },
  {
    name: "About",
    icon: aboutIcon,
    hint: "About the AI Agent platform",
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Extract environment name from document title
function getEnvironmentName() {
  if (!document.title.includes(ENVIRONMENT_CONFIG.TITLE_SEPARATOR)) {
    return ENVIRONMENT_CONFIG.DEFAULT_ENV;
  }
  const parts = document.title.split(ENVIRONMENT_CONFIG.TITLE_SEPARATOR);
  return parts.length > 1
    ? parts[1].trim().toUpperCase()
    : ENVIRONMENT_CONFIG.DEFAULT_ENV;
}

export default function Navbar({openAboutModal}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});
  const {showMenu} = useMenu();
  const [userRoles, setUserRoles] = useState([]);
  const [userHasRoles, setUserHasRoles] = useState(false);
  const environmentName = getEnvironmentName();

  useEffect(() => {
    const fetchRoles = async () => {
      const roles = await getMyRoles();
      setUserRoles(roles);
      setUserHasRoles(roles.length > 0);
    };
    fetchRoles();
    loader.init();
  }, []);

  const isMenuItemActive = (item, pathname) => {
    if (item.href === pathname) return true;
    if (item.submenu) {
      return item.submenu.some(
        (subItem) =>
          subItem.href === pathname ||
          (subItem.submenu &&
            subItem.submenu.some((grandChild) => grandChild.href === pathname)),
      );
    }
    return false;
  };

  const toggleSubmenu = (itemName, depth) => {
    setOpenMenus((prev) => {
      const newOpenMenus = {...prev};
      Object.keys(newOpenMenus).forEach((key) => {
        if (key.split("-").length - 1 === depth && key !== itemName) {
          delete newOpenMenus[key];
        }
      });
      if (newOpenMenus[itemName]) {
        delete newOpenMenus[itemName];
      } else {
        newOpenMenus[itemName] = true;
      }
      return newOpenMenus;
    });
  };

  const renderMenuItem = (item, depth = 0, parentPath = "", close) => {
    // Check the user's roles against the item's roles
    if (item.roles && !item.roles.some((role) => userRoles.includes(role))) {
      return null;
    }

    const isActive = isMenuItemActive(item, location.pathname);
    const fullPath = parentPath ? `${parentPath}-${item.name}` : item.name;
    const isOpen = openMenus[fullPath];

    const menuItemContent = (
      <span className="flex items-center">
        {item.icon && renderMenuIcon(item.icon)}
        {item.name}
      </span>
    );

    return (
      <Fragment>
        {item.name === "About" ? (
          <button
            onClick={() => {
              close();
              openAboutModal();
            }}
            className="flex items-center justify-between w-full rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-600 hover:text-white"
            title={item.hint}
          >
            {menuItemContent}
          </button>
        ) : item.submenu ? (
          <div className="relative">
            <button
              onClick={() => toggleSubmenu(fullPath, depth)}
              className={classNames(
                "flex items-center justify-between w-full rounded-md px-3 py-2 text-sm font-medium",
                isActive
                  ? "bg-gray-900 text-white font-bold"
                  : "text-gray-300 hover:bg-gray-600 hover:text-white",
              )}
              title={item.hint}
            >
              {menuItemContent}
              {isOpen ? (
                <ChevronDownIcon
                  className="ml-2 -mr-1 h-5 w-5"
                  aria-hidden="true"
                />
              ) : (
                <ChevronLeftIcon
                  className="ml-2 -mr-1 h-5 w-5"
                  aria-hidden="true"
                />
              )}
            </button>
            {isOpen && (
              <div
                className="absolute top-0 w-56 mt-0 mr-1 rounded-md bg-gray-700 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-600 sm:right-full z-10">
                <div className="py-1">
                  {item.submenu.map((subItem) => (
                    <div key={subItem.name}>
                      {renderMenuItem(subItem, depth + 1, fullPath, close)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <Link
            key={item.name}
            to={item.href}
            className={classNames(
              "block rounded-md px-3 py-2 text-sm font-medium",
              isActive
                ? "bg-gray-900 text-white font-bold"
                : "text-gray-300 hover:bg-gray-600 hover:text-white",
            )}
            aria-current={isActive ? "page" : undefined}
            onClick={() => {
              close();
              setOpenMenus({});
            }}
            title={item.hint}
          >
            {menuItemContent}
          </Link>
        )}
        {item.dividerAfter && (
          <div className="px-3 py-1">
            <hr className="px-3 py-1 border-t border-gray-600"/>
          </div>
        )}
      </Fragment>
    );
  };

  const handleLogoClick = () => {
    if (userHasRoles) {
      navigate("/");
    }
  };

  const handleContextMenu = (e) => {
    if (e.target.classList.contains("navbar-context-menu")) {
      e.preventDefault();
      showMenu(
        [
          {label: "Navigator", onClick: () => navigate("/")},
          {label: "Reference", onClick: () => navigate("/doc")},
        ],
        e.clientX,
        e.clientY,
      );
    }
  };

  const shouldHideLogoLabel = () => {
    return (
      (location.pathname.includes("/topics") &&
        location.search.includes("key")) ||
      (location.pathname.includes("/simulants") &&
        location.search.includes("key"))
    );
  };

  return (
    <div className="navbar-context-menu" onContextMenu={handleContextMenu}>
      <Disclosure as="nav">
        {({open}) => (
          <>
            <div className="px-2 sm:px-6 lg:px-8">
              <div className="navbar-context-menu relative flex h-16 items-center justify-between">
                {/* Logo */}
                <div
                  className={classNames(
                    "flex items-center",
                    userHasRoles ? "cursor-pointer" : "",
                  )}
                  onClick={handleLogoClick}
                >
                  <img
                    className="h-8 w-auto"
                    src="/logo128.svg"
                    alt="Lumeris"
                  />
                  {!shouldHideLogoLabel() && (
                    <div className="relative ml-6 mr-6">
                      <span className="font-sol-w01 font-medium text-white text-stretch-wider hidden lg:block">
                        AI AGENT
                      </span>
                      <span
                        className="text-[0.65rem] font-medium hidden lg:block absolute"
                        style={{
                          color: "#f0b557",
                          right: "0",
                          bottom: "-0.25rem",
                          transform: "translateX(100%)",
                          paddingLeft: "0.75rem",
                        }}
                      >
                        {environmentName}
                      </span>
                    </div>
                  )}
                </div>
                {/* Right side items (hamburger menu and profile) */}
                <div className="flex items-center z-10">
                  {/* Hamburger menu */}
                  {userHasRoles && (
                    <Menu
                      as="div"
                      className="relative inline-block text-left mr-0 lg:mr-4"
                    >
                      {({open, close}) => (
                        <>
                          <Menu.Button
                            className="text-gray-300 hover:bg-gray-600 inline-flex items-center justify-center rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                            onClick={() => setOpenMenus({})}
                          >
                            <Bars3Icon className="h-6 w-6" aria-hidden="true"/>
                          </Menu.Button>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items
                              className="rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-600 absolute right-0 mt-2 w-56 origin-top-right bg-gray-700 z-10">
                              <div className="py-1">
                                {navigation.map((item) => (
                                  <div key={item.name}>
                                    {renderMenuItem(item, 0, "", close)}
                                  </div>
                                ))}
                              </div>
                            </Menu.Items>
                          </Transition>
                        </>
                      )}
                    </Menu>
                  )}
                  {/* Profile dropdown */}
                  <Menu as="div" className="relative ml-0 lg:ml-3">
                    <div>
                      <Menu.Button
                        className="flex rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                        <span className="sr-only">Open user menu</span>
                        <User>
                          {({name}) => (
                            <UserIcon
                              name={name}
                              size="sm"
                              bgColor="bg-blue-500"
                            />
                          )}
                        </User>
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items
                        className="shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-600 absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-gray-700 py-1 z-20">
                        {userHasRoles && (
                          <Menu.Item>
                            <a
                              href="/profile"
                              className={classNames(
                                location.pathname.includes("/profile")
                                  ? "bg-gray-900 text-white font-bold"
                                  : "text-gray-300 hover:bg-gray-600 hover:text-white",
                                "block px-4 py-2 text-sm",
                              )}
                            >
                              Profile
                            </a>
                          </Menu.Item>
                        )}
                        <div className="px-3 py-1">
                          <hr className="px-3 py-1 border-t border-gray-600"/>
                        </div>
                        <Menu.Item>
                          <a
                            href={
                              location.pathname
                                .toLowerCase()
                                .startsWith("/champions")
                                ? "/logout?redirectUrl=/champions"
                                : "/logout"
                            }
                            className={classNames(
                              "text-gray-300 hover:bg-gray-600 hover:text-white",
                              "block px-4 py-2 text-sm",
                            )}
                          >
                            Log Out
                          </a>
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
            </div>
          </>
        )}
      </Disclosure>
    </div>
  );
}
